import { useState } from "react";
import { Link } from "react-router-dom";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { PencilSimple, ArrowSquareOut } from "@phosphor-icons/react";
import { toast } from "sonner";
import { hm, money } from "./hrUtils";

/** Everyone in the admin team with allowance, hours vs contracted and status; Ops edit allowance, hours and rate. */
export const TeamTable = ({ team, reload }) => {
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await API.put(`/hr/profiles/${edit.user_id}`, { leave_allowance_days: Number(edit.leave_allowance_days), contracted_hours_per_week: Number(edit.contracted_hours_per_week), rate_type: edit.rate_type, rate: Number(edit.rate) });
      toast.success(`${edit.name} updated`); setEdit(null); reload();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-border overflow-x-auto" data-testid="hr-team-table">
      <table className="w-full text-sm">
        <thead className="bg-muted/60 text-[11px] uppercase tracking-wide text-muted-foreground">
          <tr><th className="text-left px-3 py-2">Person</th><th className="text-left px-3 py-2">Today</th><th className="text-right px-3 py-2">This week</th><th className="text-right px-3 py-2">Leave left</th><th className="text-right px-3 py-2">Rate</th><th className="text-right px-3 py-2">Waiting</th><th className="px-3 py-2" /></tr>
        </thead>
        <tbody className="divide-y divide-border">
          {team.map((m) => {
            const over = m.week_minutes - m.contracted_minutes;
            return (
              <tr key={m.user_id} className="hover:bg-muted/30" data-testid={`hr-team-row-${m.user_id}`}>
                <td className="px-3 py-2">
                  <Link to={`/hr?user_id=${m.user_id}`} className="font-medium hover:underline flex items-center gap-1">{m.name} <ArrowSquareOut size={12} className="text-muted-foreground" /></Link>
                  <p className="text-xs text-muted-foreground">{m.role?.replace("_", " ")}{m.operations_director ? " · Ops Director" : ""}</p>
                </td>
                <td className="px-3 py-2">
                  {m.sick ? <span className="text-red-700 font-medium">Off sick</span> : m.off_today ? <span className="text-emerald-700 font-medium">On leave</span> : m.clocked_in ? <span className="text-primary font-medium">Clocked in</span> : <span className="text-muted-foreground">Not clocked in</span>}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  <span>{hm(m.week_minutes)}</span>
                  <p className={`text-[11px] ${over > 0 ? "text-amber-700" : "text-muted-foreground"}`}>{m.contracted_minutes ? (over >= 0 ? `+${hm(over)}` : `−${hm(-over)}`) + ` vs ${hm(m.contracted_minutes)}` : "no contract hours"}</p>
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{m.leave.remaining}d <span className="text-muted-foreground text-xs">/ {m.leave.allowance}</span></td>
                <td className="px-3 py-2 text-right tabular-nums">{m.profile.rate ? `${money(m.profile.rate)}/${m.profile.rate_type === "hourly" ? "hr" : "day"}` : <span className="text-muted-foreground">—</span>}</td>
                <td className="px-3 py-2 text-right text-xs">
                  {m.pending_leave > 0 && <span className="rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 mr-1">{m.pending_leave} leave</span>}
                  {m.submitted_invoices > 0 && <span className="rounded-full bg-blue-100 text-blue-800 px-2 py-0.5">{m.submitted_invoices} invoice</span>}
                </td>
                <td className="px-3 py-2 text-right"><Button size="sm" variant="ghost" onClick={() => setEdit({ ...m.profile, name: m.name, user_id: m.user_id })} data-testid={`hr-team-edit-${m.user_id}`}><PencilSimple size={14} /></Button></td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{edit?.name}</DialogTitle><DialogDescription>Allowance, contracted hours and the rate their monthly invoice is built at.</DialogDescription></DialogHeader>
          {edit && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Annual leave (days)</Label><Input type="number" step="0.5" value={edit.leave_allowance_days} onChange={(e) => setEdit({ ...edit, leave_allowance_days: e.target.value })} data-testid="hr-profile-allowance" /></div>
              <div><Label>Contracted hours / week</Label><Input type="number" step="0.5" value={edit.contracted_hours_per_week} onChange={(e) => setEdit({ ...edit, contracted_hours_per_week: e.target.value })} data-testid="hr-profile-hours" /></div>
              <div><Label>Rate type</Label>
                <Select value={edit.rate_type} onValueChange={(v) => setEdit({ ...edit, rate_type: v })}>
                  <SelectTrigger data-testid="hr-profile-rate-type"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="hourly">Hourly</SelectItem><SelectItem value="day">Day rate</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Rate (£)</Label><Input type="number" step="0.01" value={edit.rate} onChange={(e) => setEdit({ ...edit, rate: e.target.value })} data-testid="hr-profile-rate" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEdit(null)}>Cancel</Button>
            <Button onClick={save} disabled={busy} data-testid="hr-profile-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
